"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Icons } from "@/components/icons";
import { useState } from "react";
import { useTranslations } from "@/lib/hooks/use-translations";
import { FeedbackModal } from "@/components/feedback-modal";

export default function ContactFormClient() {
  const { t } = useTranslations();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    agree: "",
    fax: "", // Honeypot field
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if(!emailError) {
    setIsLoading(true);
    setError(null);
    setIsSuccess(false);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }

      setIsSuccess(true);
      setFormData({
        firstName: "",
        lastName: "",
        company: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        agree: "",
        fax: "",
      });
      setShowDialog(true);

    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred.');
      setShowDialog(true);
    } finally {
      setIsLoading(false);
    }
    }
  };
  const validateEmail = (email: any) => {
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? setEmailError(false) : setEmailError(true)
    
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-4" aria-label="Contact form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="firstName" className="text-sm">{t('pages.contact.form.firstName')}<span className="text-[red]">*</span></Label>
          <Input
            id="firstName"
            placeholder={t('pages.contact.form.firstName')}
            value={formData.firstName}
            onChange={(e) =>
              setFormData({ ...formData, firstName: e.target.value })
            }
            required
            aria-required="true"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="lastName" className="text-sm">{t('pages.contact.form.lastName')}<span className="text-[red]">*</span></Label>
          <Input
            id="lastName"
            placeholder={t('pages.contact.form.lastName')}
            value={formData.lastName}
            onChange={(e) =>
              setFormData({ ...formData, lastName: e.target.value })
            }
            required
            aria-required="true"
            className="mt-1.5"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="company" className="text-sm">{t('pages.contact.form.company')}<span className="text-[red]">*</span></Label>
          <Input
            id="company"
            placeholder={t('pages.contact.form.company')}
            value={formData.company}
            onChange={(e) =>
              setFormData({ ...formData, company: e.target.value })
            }
            required
            aria-required="true"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="phone" className="text-sm">{t('pages.contact.form.phone')}</Label>
          <Input
            id="phone"
            type="tel"
            placeholder={t('pages.contact.form.phone')}
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            aria-describedby="phone-description"
            className="mt-1.5"
          />
          <span id="phone-description" className="text-xs text-muted-foreground">
            {t('pages.contact.form.phoneOptional')}
          </span>
        </div>
      </div>

      <div>
        <Label htmlFor="email" className="text-sm">{t('pages.contact.form.email')}<span className="text-[red]">*</span></Label>
        <Input
          id="email"
          type="email"
          placeholder={t('pages.contact.form.email')}
          value={formData.email}
          onChange={(e) => {setFormData({ ...formData, email: e.target.value }); validateEmail(e.target.value);}}
          required
          aria-required="true"
          aria-describedby="email-description"
          className={`${emailError ? 'focus-visible:ring-[red] border-[red]' : ''} mt-1.5`}
        />
        {emailError && <span className="text-sm text-[red] block mt-2">{t('pages.contact.form.emailError')}</span>}
        <span id="email-description" className="sr-only">
          We will use this email to respond to your inquiry and provide updates about our services
        </span>
      </div>

      <div>
        <Label htmlFor="subject" className="text-sm">{t('pages.contact.form.subject.label')}<span className="text-[red]">*</span></Label>
        <div className="relative">
          <select
            id="subject"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background pr-8 overflow-hidden text-ellipsis
            file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none [&>option]:overflow-hidden [&>option]:text-ellipsis
            focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1.5 appearance-none"
            value={formData.subject}
            onChange={(e) =>
              setFormData({ ...formData, subject: e.target.value })
            }
            required
            aria-required="true"
          >
            <option value="">{t('pages.contact.form.subject.placeholder')}</option>
            <option value="automation">{t('pages.contact.form.subject.options.automation')}</option>
            <option value="ai-consulting">{t('pages.contact.form.subject.options.aiConsulting')}</option>
            <option value="technical-support">{t('pages.contact.form.subject.options.technicalSupport')}</option>
            <option value="project">{t('pages.contact.form.subject.options.project')}</option>
            <option value="other">{t('pages.contact.form.subject.options.other')}</option>
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
            <svg className="h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="message" className="text-sm">{t('pages.contact.form.message.label')}<span className="text-[red]">*</span></Label>
        <textarea
          id="message"
          className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1.5"
          placeholder={t('pages.contact.form.message.placeholder')}
          value={formData.message}
          onChange={(e) =>
            setFormData({ ...formData, message: e.target.value })
          }
          required
          aria-required="true"
        />
      </div>

      {/* Honeypot Field: Visually hidden but accessible to bots */}
      <div className="absolute left-[-5000px]" aria-hidden="true">
        <Label htmlFor="fax">Fax</Label>
        <Input
          id="fax"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={formData.fax}
          onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
          placeholder="Please leave this field blank"
        />
      </div>

      <div className="mt-4">
        <Button 
          type="submit" 
          variant="default" 
          size="lg" 
          className="w-full" 
          disabled={isLoading}
          aria-label={isLoading ? t('pages.contact.form.sending') : t('common.buttons.getStarted')}
        >
          {isLoading && <Icons.spinner className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {t('common.buttons.getStarted')}
        </Button>

        <p className="mt-4 cursor-pointer text-xs text-muted-foreground text-center flex sm:items-center justify-center gap-2">
          <Input
            id="agree"
            type="checkbox"
            required
            aria-required="true"
            value={formData.agree}
            onChange={(e) => setFormData({ ...formData, agree: e.target.value })}
            className="w-4 cursor-pointer min-w-4 z-10 h-4 rounded-[3px] appearance-none p-0  checked:bg-tick relative bg-contain checked:border-primary"
          />
          <Label className="cursor-pointer" htmlFor="agree">{t('pages.contact.form.acceptTerms')}<span className="text-[red]">*</span></Label>
          
        </p>
        
        {/* Temporarily commented out
        <div className="flex space-x-2">
          <a href="https://wa.me/491234567890" target="_blank" rel="noopener noreferrer" className="flex-1">
            <Button type="button" variant="outline" size="lg" className="w-full">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
              {t('pages.contact.form.callUs')}
            </Button>
          </a>
          <a href="https://wa.me/491234567890" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-12 h-12 bg-green-500 text-white rounded-md">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </a>
        </div>
        */}
      </div>

      <FeedbackModal
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        isSuccess={isSuccess}
        error={error}
      />
    </form>
  );
}
